import { Button } from "@/components/Button";
import { P } from "@/components/Typography";
import { useOnboardingProgression } from "@/requests/hooks/mat";
import { useQueryClient } from "@tanstack/react-query";
import { Trans } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { tv } from "tailwind-variants";

const styles = tv({
  slots: {
    wrapper:
      "flex flex-col items-center gap-30 rounded bg-primary-light p-20 md:flex-row",
    text: "flex-grow",
    button: "flex-shrink-0",
  },
});

interface IProps {
  enabled: boolean;
  currentStep: IMatOnboardingStep;
}

const OnboardingProgression = ({ enabled, currentStep }: IProps) => {
  const { wrapper, text, button } = styles();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { integration } = useParams();

  const getCurrentStepConfig = () => {
    const mapping = {
      INSTALL_DATASHIPS_APP: {
        index: 1,
        nextPage: "/integrations/mat-settings",
      },
      INSTALL_MAT_APP: {
        index: 2,
        nextPage: !!integration
          ? `/integrations/mat-settings/${integration}/configuration`
          : "/integrations/mat-settings",
      },
      CONFIGURE_MAT_APP: {
        index: 3,
        nextPage: "/integrations/shop-settings/privacy-details",
      },
      CONFIGURE_PRIVACY_DETAILS: {
        index: 4,
        nextPage: "/integrations/shop-settings/disable-checkout",
      },
      CONFIGURE_CHECKOUT: {
        index: 5,
        nextPage: "/integrations/shop-settings/installation",
      },
      CONFIGURE_DATASHIPS_APP: {
        index: 6,
        nextPage: "/integrations/checklist",
      },
    };

    return mapping[currentStep];
  };

  const { index, nextPage } = getCurrentStepConfig();

  const {
    mutate: customerSetupChecklistComplete,
    isPending: isCustomerSetupChecklistCompletePending,
  } = useOnboardingProgression(queryClient, () => navigate(nextPage));

  const completeStep = () => customerSetupChecklistComplete(currentStep);

  return (
    <div className={wrapper()}>
      <div className={text()}>
        <Trans
          i18nKey="components.onboardingProgession.label"
          values={{ step: index }}>
          <P>
            <strong>X/X.</strong> By clicking next you will be brought to the
            next step of onboarding
          </P>
        </Trans>
      </div>
      <div className={button()}>
        <Button
          width="sm"
          as="button"
          variant="primary"
          disabled={!enabled}
          loading={isCustomerSetupChecklistCompletePending}
          onClick={completeStep}>
          Next
        </Button>
      </div>
    </div>
  );
};

export default OnboardingProgression;
